import { Css, SelectField, useBreakpoint, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { ReactNode, useMemo } from "react";
import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { pendingProposedValue, resolveAiProposalStatus } from "src/components/formFields/ai/utils/bindAiProposal";
import { useAiNamedOptionProposal } from "src/components/formFields/ai/utils/useAiFieldProposalState";
import { MaterialAttributeDimensionType, MaterialType } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  MaterialCatalogMetadataDocument,
  MaterialCatalog_MaterialAttributeDimensionFragment,
  useSaveMavMutation,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { ListFieldState } from "src/utils/formState";
import { AddProductFormValue } from "../../design-catalog/design-package-configurator/components/AddProductPage";
import { MaterialVariantFormValue } from "./materialListingForm";

type MaterialDimensionsFormProps = {
  type: MaterialType | null | undefined;
  variant: ObjectState<MaterialVariantFormValue> | ObjectState<AddProductFormValue>;
  /** This is all available dimensions within the `Item`. */
  dimensions: MaterialCatalog_MaterialAttributeDimensionFragment[];
  /** When true, render equal-width 2-column grid that fills the parent. Otherwise use the default 150px width per select. */
  showColumns?: boolean;
  /** The variant's AI proposals, if any; a dimension's is the one named after it. Only the AI import passes these. */
  aiProposals?: ListFieldState<AiFieldProposalFormValue>;
};

export function MaterialDimensionsForm(props: MaterialDimensionsFormProps) {
  const { type, variant, dimensions, showColumns, aiProposals } = props;
  const { sm } = useBreakpoint();
  // Create a map of `dimensionId` to `mavId` to drive our per-dim Select
  const currentMavByDimension = useComputed(() => {
    return dimensions
      .map((d) => {
        const thisDimsValues = (d.values ?? []).keyBy((mav) => mav.id);
        // The dimension will have a lot of values; find the `formState.mavIds` for this dimension
        return [d.id, variant.mavIds.value?.map((mavId) => thisDimsValues[mavId]).compact().first?.id] as const;
      })
      .toObject();
  }, [dimensions, variant]);
  const [saveMavMutation] = useSaveMavMutation();

  const readOnly = useComputed(() => variant.readOnly, [variant]);

  const disabled = useComputed(
    () =>
      !readOnly && isMaterialVariantFormValueState(variant) && disableBasedOnPotentialOperation(variant.canEdit.value!),
    [variant, readOnly],
  );

  function updateMavSelection(dimensionId: string, mavId: string | undefined) {
    // Don't worry about updating currentMavByDimension; it will recompute
    const oldMavIdForDim = currentMavByDimension[dimensionId];
    // Remove old, add new...
    variant.mavIds.value = [
      ...(variant.mavIds.value ?? []).filter((mavId) => mavId !== oldMavIdForDim),
      ...(mavId ? [mavId] : []),
    ];
  }

  // Saves a new MAV and updates form state to select the new id, which it returns so the caller
  // can resolve any AI proposals.
  async function saveNewMAV(dimensionId: string, textValue: string) {
    const { data } = await saveMavMutation({
      variables: { input: { dimensionId, textValue } },
      refetchQueries: [MaterialCatalogMetadataDocument],
    });
    const mavId = data?.saveMaterialAttributeValue.materialAttributeValue.id;
    updateMavSelection(dimensionId, mavId);
    return mavId;
  }

  // Placeholder Materials dont care about MADs with useInTakeoff: false
  const mads = useMemo(
    () =>
      dimensions
        .filter((d) => type !== MaterialType.Placeholder || d.useInTakeoff)
        .filter((mad) => !readOnly || currentMavByDimension[mad.id])
        .sortByKey("sortOrder"),
    [dimensions, type, readOnly, currentMavByDimension],
  );

  return (
    <div
      css={
        // On smaller screens, just use 1 column
        showColumns
          ? Css.dg.gtc(`repeat(${sm ? 1 : 2}, minmax(0, 1fr))`).gap3.w100.$
          : Css.df.flexWrap("wrap").gap2.aifs.$
      }
    >
      {mads.map((mad) => (
        <div key={mad.id} css={showColumns ? Css.w100.$ : Css.wPx(150).$}>
          <MaterialDimensionField
            dimension={mad}
            type={type}
            value={currentMavByDimension[mad.id]}
            readOnly={readOnly}
            disabled={disabled}
            aiProposals={aiProposals}
            onSelect={(mavId) => updateMavSelection(mad.id, mavId)}
            onAddNew={(textValue) => saveNewMAV(mad.id, textValue)}
          />
        </div>
      ))}
    </div>
  );
}

type MaterialDimensionFieldProps = {
  dimension: MaterialCatalog_MaterialAttributeDimensionFragment;
  type: MaterialType | null | undefined;
  /** The MAV currently selected for this dimension. */
  value: string | undefined;
  readOnly: boolean;
  disabled: boolean | ReactNode;
  aiProposals: ListFieldState<AiFieldProposalFormValue> | undefined;
  onSelect: (mavId: string | undefined) => void;
  /** Creates a MAV for this dimension, selects it, and resolves to its id. */
  onAddNew: (textValue: string) => Promise<string | undefined>;
};

/**
 * One dimension's select.
 *
 * Its own component so the AI proposal lookup can be a hook.
 */
function MaterialDimensionField(props: MaterialDimensionFieldProps) {
  const { dimension, type, value, readOnly, disabled, aiProposals, onSelect, onAddNew } = props;
  const { name, unitOfMeasure, values, type: madType } = dimension;
  const options = useMemo(() => (values ?? []).map((mav) => ({ id: mav.id, name: mav.textValue })), [values]);
  // The agent names the proposal after the dimension and stores the value's text; the field is that
  // value's id, so this is the same name -> id lookup the brand select does.
  const { state: afpState, parser } = useAiNamedOptionProposal(aiProposals, name, options);

  // Observer wrapper needed for `afpState.status` / `fieldValue` so the pending proposal stays in sync.
  return (
    <Observer>
      {() => (
        <SelectField
          unsetLabel=" "
          label={`${name}${unitOfMeasure?.abbreviation ? ` (${unitOfMeasure.abbreviation.toLowerCase()})` : ""}`}
          value={value}
          proposedValue={pendingProposedValue(afpState, parser)}
          readOnly={readOnly}
          disabled={disabled}
          onSelect={(mavId) => {
            onSelect(mavId);
            resolveAiProposalStatus(afpState, parser, mavId);
          }}
          options={options}
          getOptionLabel={(o) => o.name}
          getOptionValue={(o) => o.id}
          // A brand new value is never the one the agent proposed, but resolve through the same
          // path so the status is set rather than left pending.
          onAddNew={async (textValue) => resolveAiProposalStatus(afpState, parser, await onAddNew(textValue))}
          helperText={
            type === MaterialType.Placeholder && madType.code === MaterialAttributeDimensionType.Number ? (
              <div css={Css.pl1.$}>eg. 10, 10-20, &gt;10</div>
            ) : null
          }
        />
      )}
    </Observer>
  );
}

function isMaterialVariantFormValueState(
  variant: ObjectState<MaterialVariantFormValue> | ObjectState<AddProductFormValue>,
): variant is ObjectState<MaterialVariantFormValue> {
  return "canEdit" in variant.value;
}
